import React, {Component, Suspense} from 'react';
import {Carousel} from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import {toast} from 'react-toastify';
import uuidv4 from 'uuid/v4';
import {AudioFile, dragDrop} from '../../../assets/images';
import '../../../assets/scss/image-gallery-custom.scss';
import Config from '../../../config';
import {INLINE_FLEX, FEATURE_RESTRICTION_ERROR_MESSAGE} from '../../../Helpers/Constants';
import {
    getMessageType,
    getThumbBase64URL,
    millisToMinutesAndSeconds,
    blockOfflineMsgAction,
    generateImageThumbnail,
    convertVideoFile,
} from '../../../Helpers/Utility';
import {getExtension, sendErrorMessage, validateFile} from '../../WebChat/Common/FileUploadValidation';
import getFileFromType from '../Conversation/Templates/Common/getFileFromType';
import Spinner from '../Conversation/Templates/Common/Spinner';
import PreviewAudio from './Audio';
import PreviewDocument from './Document';
import Image from './Image';
import './media-attachment.scss';
import PreviewVideo from './Video';
import {MediaImageThumbData} from '../../../Actions/Media';
import Store from '../../../Store';
import PropTypes from 'prop-types';
window.URL = window.URL || window.webkitURL;
const maxAllowedMediaCount = Config.maxAllowedMediaCount;

export default class MediaPreview extends Component {
    constructor() {
        super();
        this.state = {
            mediaList: [],
            previewData: [],
            selectedFiles: [],
            isZoomed: false,
            videoProgress: 0,
            videoDuration: '',
            selectedSlide: 0,
            imageThumurls: [],
            // Only true while video files are actively being converted. Defaulting to true
            // left an empty preview (opened on drag, before files arrive) stuck on the
            // "Processing media files..." spinner.
            isConverting: false,
        };
        document.addEventListener('keydown', this.handleKeyboardActions, true);
    }

    async componentDidMount() {
        const {selectedFiles: {filesId, mediaType} = {}} = this.props;
        if (!filesId) return;

        let files = Array.from(this.props.selectedFiles.files);

        // Check file limit before conversion and slice if needed
        const availableSlots = maxAllowedMediaCount - this.state.selectedFiles.length;
        if (files.length > availableSlots) {
            sendErrorMessage();
            files = files.slice(0, availableSlots);
        }

        this.thumbImageCreation(files);

        const hasVideosToConvert = files.some(file => file.type.startsWith('video/') && file.type !== 'video/mp4');

        if (hasVideosToConvert) {
            this.setState({isConverting: true});
        }

        // finally guarantees the spinner clears even if a conversion or handler throws,
        // so the preview can never get stuck on "Processing media files...".
        try {
            const convertedFiles = await Promise.all(
                files.map(async file => {
                    if (file.type.startsWith('video/') && file.type !== 'video/mp4') {
                        const converted = await convertVideoFile(file);
                        return converted || null;
                    }
                    return file;
                }),
            );
            this.handleFiles(convertedFiles.filter(Boolean), mediaType);
        } finally {
            this.setState({isConverting: false});
        }
    }

    async componentDidUpdate(prevProps) {
        const {
            selectedFiles: {filesId},
        } = this.props;

        const prevFileId = prevProps?.selectedFiles?.filesId;
        if (filesId !== prevFileId) {
            let files = Array.from(this.props.selectedFiles.files);

            // Check file limit before conversion and slice if needed
            const availableSlots = maxAllowedMediaCount - this.state.selectedFiles.length;
            if (files.length > availableSlots) {
                sendErrorMessage();
                files = files.slice(0, availableSlots);
            }

            const hasVideosToConvert = files.some(file => file.type.startsWith('video/') && file.type !== 'video/mp4');

            if (hasVideosToConvert) {
                this.setState({isConverting: true});
            }

            // finally guarantees the spinner clears even if a conversion or handler throws.
            try {
                const convertedFiles = await Promise.all(
                    files.map(async file => {
                        if (file.type.startsWith('video/') && file.type !== 'video/mp4') {
                            const converted = await convertVideoFile(file);
                            return converted || null;
                        }
                        return file;
                    }),
                );
                this.handleFiles(convertedFiles.filter(Boolean), '');
            } finally {
                this.setState({isConverting: false});
            }
        }
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyboardActions, true);
    }

    handleKeyboardActions = e => {
        // Check if mention view is open
        const isMentionViewOpen = document.querySelector('.mention_wraper');
        switch (e.keyCode) {
            case 13:
                this.state.selectedFiles.length > 0 && !e.shiftKey && !isMentionViewOpen && this.sendMediaFile();
                break;
            case 27:
                this.closePreview();
                break;
            case 37: {
                let getActiveClass = document.getElementById('imagePreviewContainer').querySelector('.incaption');
                !getActiveClass && this.Previous?.click();
                break;
            }
            case 39: {
                let rigthActiveClass = document.getElementById('imagePreviewContainer').querySelector('.incaption');
                !rigthActiveClass && this.Next?.click();
                break;
            }

            default:
                break;
        }
    };

    thumbImageCreation = async files => {
        if (files) {
            setTimeout(async () => {
                for (const key in files) {
                    if (Object.hasOwn(files, key)) {
                        const file = files[key];
                        const fileId = await file?.fileDetails?.fileId;
                        const fileName = file?.name;
                        const fileExtension = fileName.split('.').pop();
                        const fileType = getMessageType(file?.type, file);
                        if (fileType === 'image') {
                            try {
                                const thumbImage = await generateImageThumbnail(file, fileExtension);
                                const thumbImageObj = {thumbImage, fileId};
                                const thumbImageurl = getThumbBase64URL(thumbImage);
                                Store.dispatch(MediaImageThumbData(thumbImageObj));
                                this.setState(prevState => ({
                                    imageThumurls: [...prevState.imageThumurls, {thumbImageurl, fileId}],
                                }));
                            } catch (error) {
                                console.error('Error generating thumbnail:', error);
                            }
                        }
                    }
                }
            });
        }
    };

    onDragOver = event => {
        event.stopPropagation();
        event.preventDefault();
        const imagePreviewContainer = document.getElementById('imagePreviewContainer');
        imagePreviewContainer && imagePreviewContainer.classList.add('highlight');
        return false;
    };

    onDrop = async e => {
        e.preventDefault();
        const {selectedFiles: {mediaType} = {}} = this.props;
        let files = e.target.files || e.dataTransfer?.files;
        const imagePreviewContainer = document.getElementById('imagePreviewContainer');
        imagePreviewContainer && imagePreviewContainer.classList.remove('highlight');
        if (files) {
            let filesArray = Array.from(files);

            // Check file limit before conversion and slice if needed
            const availableSlots = maxAllowedMediaCount - this.state.selectedFiles.length;
            if (filesArray.length > availableSlots) {
                sendErrorMessage();
                filesArray = filesArray.slice(0, availableSlots);
            }

            this.setState(
                {
                    dragedFiles: {
                        dragId: uuidv4(),
                        files: filesArray,
                    },
                },
                async () => {
                    if (filesArray.length > 0) {
                        const hasVideosToConvert = filesArray.some(
                            file => file.type.startsWith('video/') && file.type !== 'video/mp4',
                        );

                        if (hasVideosToConvert) {
                            this.setState({isConverting: true});
                        }

                        const convertedFiles = await Promise.all(
                            filesArray.map(async file => {
                                if (file.type.startsWith('video/') && file.type !== 'video/mp4') {
                                    const converted = await convertVideoFile(file);
                                    return converted || null;
                                }
                                return file;
                            }),
                        );

                        this.setState({isConverting: false});
                        this.handleFiles(convertedFiles.filter(Boolean), mediaType);
                    }
                },
            );
        }
    };

    createAddFileContainer = (thumbsWrapper, mediaType) => {
        const {attachment: {isAttachmentEnabled = false} = {}} = this.props;
        let child = document.createElement('div');
        if (mediaType === 'imagevideo') {
            child.innerHTML = `<div id="dynamicUpload" class="uploadfile" style="display: ${
                this.state.selectedFiles.length >= maxAllowedMediaCount ? 'none' : INLINE_FLEX
            }">
            <div><label for="addfile"><input id="addfile" class="uploadfileinput" type="file"
            accept=".mp4,.x-m4v,.png,.jpeg,.jpg,video/x-m4v,.mov" multiple /></label>
            <i class="addIcon"></i><span>ADD FILE</span></div></div>`;
        } else if (mediaType === 'image') {
            child.innerHTML = `<div id="dynamicUpload" class="uploadfile" style="display: ${
                this.state.selectedFiles.length >= maxAllowedMediaCount ? 'none' : INLINE_FLEX
            }">
              <div><label for="addfile"><input id="addfile" class="uploadfileinput" type="file"
              accept=".png,.jpeg,.jpg" multiple /></label>
              <i class="addIcon"></i><span>ADD FILE</span></div></div>`;
        } else if (mediaType === 'video') {
            child.innerHTML = `<div id="dynamicUpload" class="uploadfile" style="display: ${
                this.state.selectedFiles.length >= maxAllowedMediaCount ? 'none' : INLINE_FLEX
            }">
              <div><label for="addfile"><input id="addfile" class="uploadfileinput" type="file"
              accept=".mp4,.x-m4v,video/x-m4v,.mov" multiple /></label>
              <i class="addIcon"></i><span>ADD FILE</span></div></div>`;
        } else if (mediaType === 'audio') {
            child.innerHTML = `<div id="dynamicUpload" class="uploadfile" style="display: ${
                this.state.selectedFiles.length >= maxAllowedMediaCount ? 'none' : INLINE_FLEX
            }">
              <div><label for="addfile"><input id="addfile" class="uploadfileinput" type="file"
              accept=".wav,.mp3,.aac" multiple /></label>
              <i class="addIcon"></i><span>ADD FILE</span></div></div>`;
        } else if (mediaType === 'file') {
            child.innerHTML = `<div id="dynamicUpload" class="uploadfile" style="display: ${
                this.state.selectedFiles.length >= maxAllowedMediaCount ? 'none' : INLINE_FLEX
            }">
              <div><label for="addfile"><input id="addfile" class="uploadfileinput" type="file"
              accept=".pdf,.xlsx,.xls,.txt,.doc, .docx, text/plain, .csv, .ppt, .zip, .rar, .pptx, .html, text/html,.dxf,.dwg,.dwt,.stl,.igs,.iges,.stp,.step" multiple /></label>
              <i class="addIcon"></i><span>ADD FILE</span></div></div>`;
        } else if (isAttachmentEnabled) {
            child.innerHTML = `<div id="dynamicUpload" class="uploadfile" style="display: ${
                this.state.selectedFiles.length >= maxAllowedMediaCount ? 'none' : INLINE_FLEX
            }">
                <div><label for="addfile"><input id="addfile" class="uploadfileinput" type="file"
                multiple /></label>
                <i class="addIcon"></i><span>ADD FILE</span></div></div>`;
        } else {
            toast.error(FEATURE_RESTRICTION_ERROR_MESSAGE);
            return this.closePreview();
        }

        child = child.firstChild;
        child.querySelector('.uploadfileinput').addEventListener('change', async event => {
            let files = Array.from(event.target.files);

            // Check file limit before conversion and slice if needed
            const availableSlots = maxAllowedMediaCount - this.state.selectedFiles.length;
            if (files.length > availableSlots) {
                sendErrorMessage();
                files = files.slice(0, availableSlots);
            }

            const hasVideosToConvert = files.some(file => file.type.startsWith('video/') && file.type !== 'video/mp4');

            if (hasVideosToConvert) {
                this.setState({isConverting: true});
            }

            const convertedFiles = await Promise.all(
                files.map(async file => {
                    if (file.type.startsWith('video/') && file.type !== 'video/mp4') {
                        const converted = await convertVideoFile(file);
                        return converted || null;
                    }
                    return file;
                }),
            );

            this.setState({isConverting: false});
            this.handleFiles(convertedFiles.filter(Boolean), mediaType, 'new');
        });
        thumbsWrapper.appendChild(child);
        let sendButtonChild = document.createElement('div');
        sendButtonChild.innerHTML = `<div class="attachmentSend"><i>
    <svg width="35" height="35" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M23.1754 4.25009L10.2093 8.55778C1.49339 11.4727 1.49339 16.2255 10.2093 19.126L14.0575 20.4039L15.3354 24.2521C18.236 32.968 23.0031 32.968 25.9036 24.2521L30.2257 11.3003C32.1498 5.48496 28.9908 2.31163 23.1754 4.25009ZM23.6349 11.9752L18.1785 17.4603C17.9631 17.6757 17.6903 17.7762 17.4175 17.7762C17.1447 17.7762 16.8719 17.6757 16.6565 17.4603C16.2401 17.0439 16.2401 16.3547 16.6565 15.9383L22.1129 10.4532C22.5293 10.0368 23.2185 10.0368 23.6349 10.4532C24.0513 10.8696 24.0513 11.5588 23.6349 11.9752Z" fill="white"/>
</svg></i></div>`;
        sendButtonChild = sendButtonChild.firstChild;
        sendButtonChild.querySelector('i').addEventListener('click', event => {
            this.sendMediaFile();
        });
        thumbsWrapper.parentElement.appendChild(sendButtonChild);
    };

    /**
     * @param {any} files - Selected Files
     * @param {any} mediaType - To Handle Corresponding Error Message for Different Medias
     * @param {any} type - To Handle Selected Slides When New Media Added
     */
    handleFiles = (files, mediaType, type) => {
        // Clear the drag overlay here so it lifts no matter which path delivered the files.
        // A quick drop lands on the conversation container, not this modal, so onDrop
        // (which normally removes 'highlight') never fires and the overlay would stay stuck.
        document.getElementById('imagePreviewContainer')?.classList.remove('highlight');
        let filesArray = Array.from(files);
        const {selectedFiles} = this.state;
        const { typingMessage = "" } = this.props;

        validateFile(filesArray, mediaType)
            .then(res => {
                const filterValid = filesArray.filter((file, index) => {
                    return res.find((ele, responseIndex) => responseIndex === index && ele);
                });
                if (filterValid.length + this.state.selectedFiles.length > maxAllowedMediaCount) {
                    sendErrorMessage();
                }
                this.setState(
                    {
                        showAvailableOptions: false,
                        selectedFiles: [
                            ...selectedFiles,
                            ...filterValid.map((file, index) => {
                                let fileId = uuidv4();
                                file.fileDetails = {
                                    fileId: fileId,
                                };
                                const msgType = getMessageType(file.type, file);
                                if (
                                    selectedFiles.length === 0 &&
                                    index === 0 &&
                                    typingMessage &&
                                    (msgType === 'image' || msgType === 'video' || msgType === 'file')
                                ) {
                                    file.caption = typingMessage;
                                }
                                return file;
                            }),
                        ].slice(0, maxAllowedMediaCount),
                    },
                    () => {
                        this.thumbImageCreation(this.state.selectedFiles);
                        this.setState(
                            {
                                previewData: this.handleDisplayMedia(type),
                                ...(type === 'new' && {selectedSlide: selectedFiles.length}),
                            },
                            () => {
                                const thumbsWrapper = document
                                    .getElementById('imagePreviewContainer')
                                    .querySelector('.thumbs-wrapper');
                                const uploadcontainer = document.getElementById('dynamicUpload');
                                if (thumbsWrapper && !uploadcontainer) {
                                    this.createAddFileContainer(thumbsWrapper, mediaType);
                                } else if (uploadcontainer)
                                    uploadcontainer.style.display =
                                        this.state.selectedFiles.length >= maxAllowedMediaCount ? 'none' : INLINE_FLEX;
                            },
                        );
                    },
                );
            })
            .catch(err => {});
    };

    removeSelectedItem = deleteThis => {
        const {selectedFiles, selectedSlide} = this.state;
        let index = selectedFiles.indexOf(deleteThis);
        const filterItem = selectedFiles.filter((elem, i) => i !== index);
        let newActiveSlide =
            selectedSlide !== 0 && selectedSlide === filterItem.length ? selectedSlide - 1 : selectedSlide;

        this.setState(
            {
                selectedFiles: filterItem,
                selectedSlide: newActiveSlide,
            },
            () => {
                const uploadcontainer = document.getElementById('dynamicUpload');
                if (uploadcontainer) uploadcontainer.style.display = INLINE_FLEX;
                this.setState({
                    previewData: this.handleDisplayMedia(),
                });
            },
        );
    };

    onChangeCaption = (updateThis = {}, caption = '', mentionedUsersIds = []) => {
        let temp = document.createElement('div');
        temp.innerHTML = caption;
        updateThis.caption = temp.innerHTML;
        updateThis.mentionedUsersIds = mentionedUsersIds;
        return true;
    };

    updateMedia = (updateThis, fileMetaData) => {
        const {selectedFiles} = this.state;
        let index = selectedFiles.indexOf(updateThis);
        const updateItem = selectedFiles.map((element, i) => {
            if (i === index) {
                element.fileDetails = {
                    fileId: updateThis?.fileDetails?.fileId,
                    ...fileMetaData,
                };
            }
            return element;
        });
        this.setState({
            selectedFiles: updateItem,
        });
    };

    handlePrev = (onClickHandler, hasPrev) => {
        return (
            hasPrev && (
                <button
                    type="button"
                    onClick={onClickHandler}
                    title="Previous"
                    ref={input => (this.Previous = input)}
                    aria-label="previous slide / item"
                    className="control-arrow control-prev"
                ></button>
            )
        );
    };

    handleNext = (onClickHandler, hasNext) => {
        return (
            hasNext && (
                <button
                    type="button"
                    onClick={onClickHandler}
                    title="Next"
                    ref={input => (this.Next = input)}
                    aria-label="next slide / item"
                    className="control-arrow control-next"
                ></button>
            )
        );
    };

    handleMediaOnChange = (index, item) => {
        this.setState({selectedSlide: index});
        document
            .getElementById('imagePreviewContainer')
            .querySelectorAll('video, audio')
            .forEach(element => element.pause());
    };

    sendMediaFile = () => {
        if (blockOfflineMsgAction()) return false;
        const found = this.state.selectedFiles.filter(el => {
            const {
                fileDetails: {msgType, imageUrl},
            } = el;
            return !((!msgType || msgType === 'image' || msgType === 'video ') && !imageUrl);
        });
        if (found.length !== this.state.selectedFiles.length) return true;
        this.props.onClickSend(this.state.selectedFiles);
        return true;
    };

    handleDisplayMedia = type => {
        const {selectedFiles} = this.state;
        const {selectedFiles: {chatType} = {}, chatId} = this.props;
        if (selectedFiles.length === 0) return this.closePreview();
        return selectedFiles.map((res, index) => {
            let fileType = res.type;
            let mediaType = getMessageType(res.type, res);
            const fileExtension = getExtension(res.name);
            const {
                fileDetails: {fileId},
                caption = '',
            } = res;
            switch (mediaType) {
                case 'image':
                    return (
                        <Image
                            thumb={'image'}
                            key={fileId}
                            type={type}
                            uniqueId={fileId}
                            selectedFiles={selectedFiles}
                            onClickSend={this.sendMediaFile}
                            chatType={chatType}
                            chatId={chatId}
                            media={res}
                            caption={caption}
                            onClickCloseSelectedItem={this.props.onClickCloseSelectedItem}
                            onChangeCaption={this.onChangeCaption}
                            updateMedia={this.updateMedia}
                        />
                    );
                case 'video':
                    return (
                        <PreviewVideo
                            thumb={'video'}
                            key={fileId}
                            type={type}
                            uniqueId={fileId}
                            selectedFiles={selectedFiles}
                            onClickSend={this.sendMediaFile}
                            chatType={chatType}
                            chatId={chatId}
                            media={res}
                            caption={caption}
                            onClickCloseSelectedItem={this.props.onClickCloseSelectedItem}
                            onChangeCaption={this.onChangeCaption}
                            updateMedia={this.updateMedia}
                        />
                    );
                case 'file': {
                    const placeholder = getFileFromType(fileType, fileExtension);
                    return (
                        <PreviewDocument
                            thumb={'file'}
                            key={fileId}
                            type={type}
                            uniqueId={fileId}
                            selectedFiles={selectedFiles}
                            onClickSend={this.sendMediaFile}
                            chatType={chatType}
                            chatId={chatId}
                            media={res}
                            caption={caption}
                            placeholder={placeholder}
                            onClickCloseSelectedItem={this.props.onClickCloseSelectedItem}
                            onChangeCaption={this.onChangeCaption}
                            updateMedia={this.updateMedia}
                        />
                    );
                }
                case 'audio':
                    return (
                        <PreviewAudio
                            thumb={'audio'}
                            key={fileId}
                            onClickSend={this.sendMediaFile}
                            media={res}
                            caption={caption}
                            onClickCloseSelectedItem={this.props.onClickCloseSelectedItem}
                            onChangeCaption={this.onChangeCaption}
                            updateMedia={this.updateMedia}
                        />
                    );
                default:
                    return <div key={fileId}></div>;
            }
        });
    };

    closePreview = () => {
        this.props.onClosePreview();
    };

    getSrcDocument = (thumb, placeholder) => {
        if (thumb === 'file') {
            return placeholder;
        }
        return AudioFile;
    };

    imageOnLoad = e => {
        e.target.classList.remove('image-load');
        e.target.style.display = 'block';
    };

    getMediaSrc = (thumb, placeholder, imageUrl, fileId = null) => {
        let mediaSrc;
        const foundItem = this.state.imageThumurls.find(item => item.fileId === fileId);
        switch (thumb) {
            case 'file':
            case 'audio':
                mediaSrc = this.getSrcDocument(thumb, placeholder);
                break;

            case 'video':
                mediaSrc = getThumbBase64URL(imageUrl);
                break;

            case 'image':
                mediaSrc = foundItem?.thumbImageurl ? foundItem?.thumbImageurl : null;
                break;

            default:
                break;
        }

        return mediaSrc;
    };

    customRenderThumb = children => {
        return children.map((item, index) => {
            const {
                placeholder,
                thumb,
                media: {
                    fileDetails: {fileId, imageUrl = '', duration},
                },
            } = item.props;

            const mediaSrc = this.getMediaSrc(thumb, placeholder, imageUrl, fileId);

            return (
                <div key={fileId} className={thumb === 'file' ? 'thumb-img file img-load' : 'thumb-img img-load'}>
                    <span
                        role="button"
                        tabIndex="0"
                        onClick={e => {
                            e.stopPropagation();
                            this.removeSelectedItem(item.props.media);
                        }}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                this.removeSelectedItem(item.props.media);
                            }
                        }}
                        title="Remove"
                        className="removeSelected"
                    >
                        X
                    </span>
                    <img
                        src={mediaSrc}
                        key={`${fileId}-Img`}
                        alt=""
                        onLoad={this.imageOnLoad}
                        className="image-load"
                        style={{display: 'none'}}
                    />
                    <Spinner />
                    {duration && (
                        <p
                            className={`${thumb === 'video' ? 'video-duration' : 'audio-duration'}`}
                            key={`${fileId}-Duration`}
                        >
                            {millisToMinutesAndSeconds(duration)}
                        </p>
                    )}
                </div>
            );
        });
    };

    render() {
        let {previewData, selectedFiles, selectedSlide, isConverting} = this.state;
        return (
            <Suspense fallback={<div>Loading...</div>}>
                <section
                    className="imagePreviewContainer mediaAttachmemtUpload"
                    id="imagePreviewContainer"
                    onDrop={this.onDrop}
                    onDragOver={this.onDragOver}
                    aria-label="Drag and drop file preview area"
                >
                    <div className="attachments-header">
                        <h5>Preview</h5>
                        <button
                            type="button"
                            onClick={this.closePreview}
                            onKeyDown={e => e.key === 'Enter' && this.closePreview()}
                            className="close-btn"
                            aria-label="Close Preview"
                            style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                margin: 0,
                                cursor: 'pointer',
                            }}
                        >
                            <svg width="24px" height="24px" viewBox="0 0 24 24">
                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                                <path d="M0 0h24v24H0z" fill="none" />
                            </svg>
                        </button>
                    </div>

                    {isConverting ? (
                        <div className="uploader-loading">
                            <Spinner />
                            <p>Processing media files...</p>
                        </div>
                    ) : selectedFiles.length > 0 ? (
                        <Carousel
                            renderArrowPrev={this.handlePrev}
                            renderArrowNext={this.handleNext}
                            selectedItem={selectedSlide}
                            onChange={this.handleMediaOnChange}
                            onClickThumb={this.handleMediaOnChange}
                            renderThumbs={this.customRenderThumb}
                            interval="100"
                            transitionTime="10"
                            thumbWidth="80"
                            swipeable={false}
                        >
                            {previewData}
                        </Carousel>
                    ) : (
                        <div className="drag-preview">
                            <div className="drag-area">
                                <div>
                                    <div className="drag-area-inner">
                                        <span>
                                            <img src={dragDrop} alt="Drag and Drop" />
                                        </span>
                                        <h1>Drag & Drop Your Files Here</h1>
                                        <p>
                                            Make sure to upload a 'JPG, JPEG, PNG, MP3, MP4, PDF, XLS, XLSX, DOC, DOCX,
                                            PPT, PPTX, RAR, ZIP, CSV, or TXT' file
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </section>
            </Suspense>
        );
    }
}

MediaPreview.propTypes = {
    selectedFiles: PropTypes.shape({
        fileId: PropTypes.string,
        filesId: PropTypes.string,
        mediaType: PropTypes.string,
        files: PropTypes.array,
        chatType: PropTypes.string,
    }),
    attachment: PropTypes.shape({
        isAttachmentEnabled: PropTypes.bool,
    }),
    chatId: PropTypes.string,
    onClickSend: PropTypes.func,
    onClickCloseSelectedItem: PropTypes.func,
    onClosePreview: PropTypes.func,
};
